/**
 * 接続 as the cases and examples the book lays out, from the table the deck actually holds
 * (日本-524, superseding 日本-519's `formationLines`).
 *
 * **接続 is a TABLE in the deck and always was.** `raw_formation.json` — the deck's untouched HTML
 * for the field, produced by `GrammarResource`'s `refresh_formation.py` — reports `table×522,
 * tbody×522, tr×3299, td×9887`. So 522 of the 523 entries carrying 接続 are tabular; what reached
 * this app was that table with every cell and row boundary deleted, because `build_dataset.strip_html`
 * broke on `<br>`, `</p>`, `</li>` and `</div>` and on nothing else. 間・あいだ(に) arrived as 260
 * characters of `(i) Vている間（に）話している間（に）While someone is talking食べている間（に）…` in
 * one paragraph.
 *
 * After the re-extraction the field keeps **one line per row and a TAB between cells**: 518 of 523
 * carry a newline (was **0**), 522 carry a tab, median 6 rows an entry, and the median line is 38
 * characters against that one 260-character wall.
 *
 * **This REPLACES `formationLines` rather than extending it.** That function recovered the book's
 * `(i)` markers and the runs of spaces the old extraction left where a line ended — honest work
 * against a flattened string, and there is no flattened string any more. It survives only as the
 * fallback branch below, for the one entry that carries no tab.
 *
 * ## The cell semantics, measured over all 3,300 rows
 *
 * A row is up to three cells and they are positional rather than named:
 *
 *  - the **gloss** is the last cell when it is Latin-only and is not the book's own slot
 *    vocabulary — 2,123 of 3,300 rows; 305 are blank; otherwise that cell is more pattern and
 *    there is no gloss (see `slotOnly`, which is what keeps `ただの ‖ Noun` a construction);
 *  - the **text** is every remaining non-empty cell, joined — the deck splits a pattern across
 *    cells at the point where its slot ends (`(i) Vて` ‖ `いる間（に）`) and that split is
 *    typographic, not semantic;
 *  - a leading `(i)`…`(vii)` on the first cell opens a **new case** — 343 of 523 entries;
 *  - a row **with** a gloss is an example; a row **without** one is the pattern line.
 *
 * Worked, and these two are what `check-grammar.mjs` names:
 *
 * ```
 * basic-116  (i) Vて ‖ いる間（に）                          → case (i), pattern 「Vて いる間（に）」
 *            話している間（に） ‖ While someone is talking     → example + gloss
 * basic-079  Vて ‖ しまう ‖ ·                                → no case, pattern 「Vて しまう」
 *            · ‖ 話してしまう ‖ Have talked                   → example + gloss
 * ```
 *
 * **Every character survives**, which is the same promise `formationLines` made and the reason the
 * corpus test asserts it rather than trusting it: this splits and joins, and never drops.
 */

/** A `(i)`…`(vii)` at the head of the first cell. The book numbers its cases no further than (vii). */
const CASE_LABEL = /^\((i|ii|iii|iv|v|vi|vii)\)\s*/;

/**
 * Latin-only, which is what tells a gloss from more pattern.
 *
 * **Deliberately not "contains no CJK".** A pattern cell routinely mixes scripts — `Adjective い間
 * （に）`, `おVます` — so a test that admitted anything without kana would call those glosses. The
 * question the layout actually asks is *is this cell English*, and the honest form of it is that
 * every character is ASCII or a curly quote. Measured: 2,145 rows answer yes and every one of them
 * is a translation.
 */
const LATIN_ONLY = /^[\x20-\x7e‘-”–—…]+$/;

/**
 * The book's own SLOT vocabulary — a last cell that is `Noun` or `Vinformal` is the REST OF THE
 * PATTERN, not a translation of it.
 *
 * **Latin-only is not sufficient on its own, and this is measured rather than feared.** 22 rows
 * across 16 entries end in a bare placeholder: `ただの ‖ Noun`, `いわゆる ‖ Noun`, `(i) やっと ‖ Vinformal`,
 * `(i) より ‖ Adjective`, `(vi) およそ ‖ Number(+Counter)`, `せめて ‖ Noun Particle`, `(i) つい ‖ Verb`.
 * Read as glosses they set the second half of the construction in the quiet 14px translation face
 * under the first — so ただの's 接続 box said 「ただの」 with *Noun* beneath it, which reads as a
 * translation claiming ただの means "Noun", and the construction 「ただの＋Noun」 was never drawn
 * as a construction at all.
 *
 * **The whole cell has to be slots.** A qualifier that merely mentions one is a real note the book
 * writes in English — `Where Noun indicates an event or a specific time`, `Where Noun=noun of
 * time` — and those belong in the gloss. Splitting on the punctuation the book uses inside a slot
 * (`Number(+Counter)`, `{Noun/N}`) is what lets the two be told apart by vocabulary alone.
 *
 * Measured over all 3,300 rows, exactly eight distinct cells answer yes — `Noun` (9), `Adverb`
 * (3), `Verb` (3), `Vinformal` (2), `Number(+Counter)` (2), `Adjective`, `Noun Particle` and
 * `Number+Counter` — and no translation does.
 */
const SLOT_TOKEN =
  /^(?:Noun|Verb|Adverb|Adjective|Adj|Particle|Prt|Number|Counter|Vinformal|Vmasu|Vstem|Vte|Vconditional|Vvolitional|Vpotential|Adv|N|V)\d*$/;

function slotOnly(cell: string): boolean {
  const tokens = cell.split(/[\s()+{}/]+/).filter((token) => token.length > 0);
  return tokens.length > 0 && tokens.every((token) => SLOT_TOKEN.test(token));
}

export interface FormationRow {
  /** The book's own label for this row: `すverbs`, `Vて`, `Group 1 verbs`. Null where it is blank. */
  label: string | null;
  /** The construction, or the example sentence: `話す→話して`. Never empty. */
  text: string;
  /** The book's translation, where the row carries one. */
  gloss: string | null;
}

export interface FormationCase {
  /** `(i)`, or null for an entry the book does not number — 180 of 523. */
  label: string | null;
  rows: FormationRow[];
}

/**
 * One row of the deck's table, read positionally.
 *
 * **The gloss comes off the END and the label off the FRONT, and what is left is the text.** That
 * ordering is what reconciles the two- and three-column entries without a shape test: drop the
 * gloss, and a row with two cells left is `label ‖ text` while a row with one is text alone. So
 * basic-116's `(i) Vて ‖ いる間（に）` reads as label `Vて`, text `いる間（に）` — the book's own two
 * columns — and its `話している間（に） ‖ While someone is talking` reads as text plus gloss with no
 * label, from the same rule.
 *
 * **Empty cells are positional and must not be filtered out before this runs.** basic-011's second
 * row is `· ‖ 食べてある ‖ Has been eaten`: filtering the blank first would leave one cell and read
 * the sentence as a label.
 */
function readRow(line: string): { row: FormationRow; caseLabel: string | null } | null {
  const cells = line.split("\t").map((cell) => cell.trim());
  const last = cells[cells.length - 1] ?? "";
  const gloss = cells.length > 1 && LATIN_ONLY.test(last) && !slotOnly(last) ? last : null;
  const rest = gloss === null ? [...cells] : cells.slice(0, -1);

  let caseLabel: string | null = null;
  const head = CASE_LABEL.exec(rest[0] ?? "");
  if (head) {
    caseLabel = `(${head[1]})`;
    rest[0] = (rest[0] as string).slice(head[0].length).trim();
  }

  const filled = rest.filter((cell) => cell.length > 0);
  if (filled.length === 0 && gloss === null) return null;

  let label: string | null = null;
  let text: string;
  if (rest.length >= 2) {
    label = (rest[0] as string) || null;
    text = rest.slice(1).filter((cell) => cell.length > 0).join(" ");
  } else {
    text = filled.join(" ");
  }

  // A row whose only content was its case marker — basic-038's `(i) Group 1 verbs ‖ · ‖ ·` after
  // the label is taken — still opens the case, with the label as its text.
  if (!text) {
    if (label) {
      text = label;
      label = null;
    } else {
      // Nothing but a translation: basic-040 writes `(i) ‖ N(Prt)もN(Prt)もN(Prt)も ‖ ·` and then
      // `· ‖ · ‖ Both teachers and students` on the row below. Returned with empty text so the
      // caller can attach it to the row it belongs to; dropping it here lost the gloss outright.
      return { row: { label: null, text: "", gloss }, caseLabel };
    }
  }
  return { row: { label, text, gloss }, caseLabel };
}

/**
 * The cases 接続 lays out, in the book's order.
 *
 * **A case opens on its `(i)` marker and on nothing else.** The first cut opened one on any row
 * without a gloss and closed it on any row with one, which read basic-038's
 * `(iii) Irregular verbs ‖ 来る→来て ‖ Someone comes and` — a case heading, its construction and its
 * translation all on one row — as a mere example, and lost the `(iii)` outright. The marker is the
 * only thing the book uses to say *new case*, so it is the only thing this reads.
 *
 * **Rows before the first marker go into an unlabelled case rather than being dropped.** 180
 * entries have no marker at all, so that is the ordinary path and not a repair.
 */
export function formationCases(formation: string): FormationCase[] {
  const cases: FormationCase[] = [];
  for (const line of formation.split("\n")) {
    const read = readRow(line);
    if (!read) continue;
    if (read.caseLabel !== null || cases.length === 0) {
      cases.push({ label: read.caseLabel, rows: [] });
    }
    const open = cases[cases.length - 1] as FormationCase;

    // **A translation on a row of its own belongs to the row above it**, which is how the deck
    // writes basic-040: the construction on one row, its English on the next. Attaching rather
    // than emitting keeps `text` non-empty on every row the renderer draws, and keeps the
    // translation next to what it translates instead of below an empty cell.
    if (!read.row.text) {
      const previous = open.rows[open.rows.length - 1];
      if (read.row.gloss && previous && previous.gloss === null) previous.gloss = read.row.gloss;
      continue;
    }
    open.rows.push(read.row);
  }
  return cases;
}

/**
 * 接続 for an entry the re-extraction could not give a table — **one entry of 523**.
 *
 * This is 日本-519's `formationLines` verbatim, kept as the fallback and nothing else. Its two
 * separators were measured against the flattened field: the book's own `(i)`/`(ii)` markers (65% of
 * entries) and a run of two or more spaces (31%), which is where the old extraction left a line
 * ending. Together they took 408 of 523 from one line to several.
 *
 * **Do not extend it.** The right fix for a 接続 that arrives flat is upstream, in
 * `GrammarResource`'s extraction, which is where the 522 came from.
 */
const FORMATION_CASE = /(?=\((?:i|ii|iii|iv|v|vi)\)\s)/;
const FORMATION_GAP = /[  　 ]{2,}/;

export function formationLines(formation: string): string[] {
  return formation
    .split(FORMATION_CASE)
    .flatMap((chunk) => chunk.split(FORMATION_GAP))
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

/**
 * What the body draws: the parsed cases, or the flat lines when there is no table to read.
 *
 * The caller gets one shape or the other and renders each differently — a table cannot be faked
 * from a line, and pretending otherwise would put a one-column table on screen claiming a structure
 * the data does not have.
 */
export type Formation =
  | { kind: "cases"; cases: FormationCase[] }
  | { kind: "lines"; lines: string[] };

export function readFormation(formation: string): Formation {
  if (formation.includes("\t") || formation.includes("\n")) {
    const cases = formationCases(formation);
    if (cases.length > 0) return { kind: "cases", cases };
  }
  return { kind: "lines", lines: formationLines(formation) };
}
